import type Phaser from "phaser";
import type { EffectsDetail } from "daedalus-renderer-phaser";
import { NO_OVERRIDES, PUDDLE_CELL_PX, cutOf, fieldInto, peakOf, waterAmount, type Puddle, type PuddleClass, type PuddleOverrides, type WetState } from "odyssey-shared";

/**
 * Puddles on the page (P2.27-T20). Each puddle is a field of values (`odyssey-shared/src/weather/puddles.ts`) painted into a
 * pooled canvas texture at the level its fill sets, redrawn only when the fill moves a 32nd; puddles are found by a grid
 * over the map and only those inside the camera's view are held, each in a slot taken from a pool sized for its class. Rain
 * draws duck-pond style ripple rings on the puddles: a few rings in each, each at its own pace and place, a ring only where it
 * fits whole. Effects Reduced halves the ripple rate; Off and reduced motion keep the still puddles and draw no ripples.
 */

const FILL_STEPS = 32;
const BUCKET_PX = 512;
const BUCKET_STRIDE = 0x10000;
const VIEW_REACH = 128;
const RIPPLE_PERIOD_MS = 2600;
const RIPPLE_ACTIVE = 0.55;
const RAIN_ON_ABOVE = 0.05;
const SLOT_RIPPLES = 5;
import { HASH_A, RIPPLE_FRAMES, RIPPLE_H, RIPPLE_KEY, RIPPLE_RX, RIPPLE_RY, RIPPLE_W, drawRippleTexture, paintPuddle } from "./puddle-paint.ts";

export { PUDDLE_COLOURS, RIPPLE_FRAMES, RIPPLE_H, RIPPLE_KEY, RIPPLE_RX, RIPPLE_RY, RIPPLE_W, paintPuddleInto } from "./puddle-paint.ts";


/**
 * One ripple's state: where it is, the cycle it was placed for and whether it shows.
 */
interface Ripple {
  image: Phaser.GameObjects.Image;
  cycle: number;
  x: number;
  y: number;
  placed: boolean;
}

/**
 * A pooled slot: the canvas a puddle is painted into, its image, the field cells and the ripples.
 */
interface Slot {
  readonly cls: PuddleClass;
  readonly key: string;
  readonly texture: Phaser.Textures.CanvasTexture;
  readonly image: Phaser.GameObjects.Image;
  readonly pixels: ImageData;
  readonly cells: Float32Array;
  readonly ripples: Ripple[];
}

/**
 * A puddle held while it is on screen.
 */
interface Held {
  readonly puddle: Puddle;
  slot: Slot;
  step: number;
  threshold: number;
  rippleCount: number;
  area: number;
  alpha: number;
}

/**
 * What the view reads.
 */
export interface PuddleViewOptions {
  readonly scene: Phaser.Scene;
  readonly puddles: readonly Puddle[];
  readonly depth: number;
}

/**
 * What a frame is given. During a crossing's fade `from` is the ground shown at the crossing and `mix` how far the fade has
 * run (eased, 0 to 1): each puddle's fill runs from its fill on `from` to its fill on `ground`, and a puddle dry at either
 * end fades in or out by its opacity with the mix, so the water drawn never jumps when a flat field crosses its cut.
 */
export interface PuddleFrame {
  readonly view: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  readonly ground: WetState;
  readonly rain: number;
  readonly detail: EffectsDetail;
  readonly now: number;
  readonly dry: boolean;
  readonly overrides?: PuddleOverrides;
  readonly from?: WetState | null;
  readonly mix?: number;
}

/**
 * Puddles drawn on the ground.
 */
export class PuddleView {
  private readonly scene: Phaser.Scene;
  private depth: number;
  private readonly buckets = new Map<number, Puddle[]>();
  private readonly held = new Map<number, Held>();
  private readonly pool: Record<PuddleClass, Slot[]> = { small: [], medium: [], large: [] };
  private readonly side: Record<PuddleClass, number> = { small: 0, medium: 0, large: 0 };
  private readonly seen = new Set<number>();
  private readonly dropped = new Set<Puddle>();
  /**
   * Lets go of a held puddle that this frame did not see.
   *
   * @param held The puddle held.
   * @param id Its id.
   */
  private readonly sweep = (held: Held, id: number): void => {
    if (this.seen.has(id)) return;
    this.release(held);
    this.held.delete(id);
  };
  private count = 0;
  private drawn = 0;
  /**
   * Adds one held puddle's drawn water to the frame's sum.
   *
   * @param held The puddle held.
   */
  private readonly addWater = (held: Held): void => {
    this.drawn += held.area * held.alpha;
  };
  private probeX = 0;
  private probeY = 0;
  private probeHit = false;
  /**
   * Tests one held puddle for the point probed: sets `probeHit` when the point lies in its painted water.
   *
   * @param held The puddle held.
   */
  private readonly probe = (held: Held): void => {
    if (this.probeHit) return;
    const puddle = held.puddle;
    const cx = Math.floor((this.probeX - (puddle.x - puddle.radius)) / PUDDLE_CELL_PX);
    const cy = Math.floor((this.probeY - (puddle.y - puddle.radius)) / PUDDLE_CELL_PX);
    if (cx >= 0 && cy >= 0 && cx < puddle.radius && cy < puddle.radius && (held.slot.cells[cy * puddle.radius + cx] as number) > held.threshold) this.probeHit = true;
  };

  /**
   * @param options The scene, the map's puddles and the depth they draw at.
   */
  public constructor(options: PuddleViewOptions) {
    this.scene = options.scene;
    this.depth = options.depth;
    this.file(options.puddles);
    drawRippleTexture(this.scene);
  }

  /**
   * Takes another map's puddles at a seam crossing: lets go of every puddle held, files the new list, and keeps the pooled
   * images, set to the new depth, of every class whose largest puddle still fits them.
   *
   * @param puddles The new map's puddles.
   * @param depth The depth they draw at.
   */
  public reset(puddles: readonly Puddle[], depth: number): void {
    for (const held of this.held.values()) this.release(held);
    this.held.clear();
    this.buckets.clear();
    this.dropped.clear();
    const before = { ...this.side };
    this.side.small = 0;
    this.side.medium = 0;
    this.side.large = 0;
    this.file(puddles);
    this.depth = depth;
    for (const cls of ["small", "medium", "large"] as const) {
      const list = this.pool[cls];
      if (this.side[cls] > before[cls]) {
        for (const slot of list) this.destroySlot(slot);
        list.length = 0;
        continue;
      }
      this.side[cls] = before[cls];
      for (const slot of list) {
        slot.image.setDepth(depth);
        for (const ripple of slot.ripples) ripple.image.setDepth(depth + 0.00001);
      }
    }
  }

  /**
   * Files puddles into the view's buckets and grows each class's slot size to its largest puddle.
   *
   * @param puddles The puddles.
   */
  private file(puddles: readonly Puddle[]): void {
    for (const puddle of puddles) {
      const key = this.bucketOf(puddle.x, puddle.y);
      const list = this.buckets.get(key);
      if (list === undefined) this.buckets.set(key, [puddle]);
      else list.push(puddle);
      this.side[puddle.cls] = Math.max(this.side[puddle.cls], puddle.radius);
    }
  }

  /**
   * Destroys one pooled slot's images and texture.
   *
   * @param slot The slot.
   */
  private destroySlot(slot: Slot): void {
    for (const ripple of slot.ripples) ripple.image.destroy();
    slot.image.destroy();
    slot.texture.destroy();
  }

  /**
   * Takes the puddles a predicate names out of the view for good, letting go of any it holds: those that lie where something stands
   * on the water (under a roof).
   *
   * @param drops Whether a puddle is to go.
   */
  public drop(drops: (puddle: Puddle) => boolean): void {
    for (const [key, list] of this.buckets) {
      const kept: Puddle[] = [];
      for (const puddle of list) {
        if (drops(puddle)) this.dropped.add(puddle);
        else kept.push(puddle);
      }
      if (kept.length === list.length) continue;
      if (kept.length === 0) this.buckets.delete(key);
      else this.buckets.set(key, kept);
    }
    for (const [id, held] of this.held) {
      if (!this.dropped.has(held.puddle)) continue;
      this.release(held);
      this.held.delete(id);
    }
  }

  /**
   * Whether a puddle is still in the view, that is, was not dropped.
   *
   * @param puddle The puddle.
   * @returns True when it is.
   */
  public holds(puddle: Puddle): boolean {
    return !this.dropped.has(puddle);
  }

  /**
   * How many puddles are on screen now, for the capture.
   *
   * @returns The count.
   */
  public get shown(): number {
    return this.held.size;
  }

  /**
   * The depth the puddles draw at; what stands in the water is drawn just above it.
   *
   * @returns The depth.
   */
  public get groundDepth(): number {
    return this.depth;
  }

  /**
   * Whether a world point lies in water now: inside the painted shape of a puddle in view at its current fill.
   *
   * @param x World x in pixels.
   * @param y World y in pixels.
   * @returns True in a puddle.
   */
  public waterAt(x: number, y: number): boolean {
    this.probeX = x;
    this.probeY = y;
    this.probeHit = false;
    this.held.forEach(this.probe);
    return this.probeHit;
  }

  /**
   * Draws a frame: finds the puddles in view, holds those with something to show and lets go of the rest, repaints those whose
   * fill moved, and plays the ripples.
   *
   * @param frame The view, the wet minutes, the rain's strength, the effects detail, the time and whether the page is
   *   indoors.
   */
  public update(frame: PuddleFrame): void {
    this.seen.clear();
    if (!frame.dry) {
      const view = frame.view;
      const bx0 = Math.floor((view.x - VIEW_REACH) / BUCKET_PX);
      const bx1 = Math.floor((view.x + view.width + VIEW_REACH) / BUCKET_PX);
      const by0 = Math.floor((view.y - VIEW_REACH) / BUCKET_PX);
      const by1 = Math.floor((view.y + view.height + VIEW_REACH) / BUCKET_PX);
      for (let by = by0; by <= by1; by += 1) {
        for (let bx = bx0; bx <= bx1; bx += 1) {
          const list = this.buckets.get((by + 1) * BUCKET_STRIDE + bx + 1);
          if (list === undefined) continue;
          for (const puddle of list) {
            if (puddle.x + puddle.radius < view.x || puddle.x - puddle.radius > view.x + view.width || puddle.y + puddle.radius < view.y || puddle.y - puddle.radius > view.y + view.height) continue;
            this.show(puddle, frame);
          }
        }
      }
    }
    this.held.forEach(this.sweep);
    this.drawn = 0;
    this.held.forEach(this.addWater);
  }

  /**
   * The water drawn in the last frame: each held puddle's painted area in pixels times its opacity, for the capture.
   *
   * @returns The sum.
   */
  public get water(): number {
    return this.drawn;
  }

  /**
   * Lets go of everything and destroys the pooled images and textures.
   */
  public destroy(): void {
    for (const held of this.held.values()) this.release(held);
    this.held.clear();
    for (const list of Object.values(this.pool)) {
      for (const slot of list) this.destroySlot(slot);
      list.length = 0;
    }
    if (this.scene.textures.exists(RIPPLE_KEY)) this.scene.textures.remove(RIPPLE_KEY);
  }

  /**
   * The bucket a point falls in.
   *
   * @param x World x.
   * @param y World y.
   * @returns The bucket's key.
   */
  private bucketOf(x: number, y: number): number {
    return (Math.floor(y / BUCKET_PX) + 1) * BUCKET_STRIDE + Math.floor(x / BUCKET_PX) + 1;
  }

  /**
   * Holds a puddle in view at its fill, painting it when the fill has moved a step, and plays its ripples.
   *
   * @param puddle The puddle.
   * @param frame The frame.
   */
  private show(puddle: Puddle, frame: PuddleFrame): void {
    const override = (frame.overrides ?? NO_OVERRIDES).get(puddle.key);
    const to = waterAmount(frame.ground, puddle, override, frame.now);
    let fill = to;
    let alpha = 1;
    if (frame.from !== undefined && frame.from !== null) {
      const mix = frame.mix ?? 1;
      const from = waterAmount(frame.from, puddle, override, frame.now);
      fill = from + (to - from) * mix;
      alpha = from <= 0 ? mix : to <= 0 ? 1 - mix : 1;
    }
    if (fill <= 0 || alpha <= 0) return;
    this.seen.add(puddle.id);
    let held = this.held.get(puddle.id);
    if (held === undefined) {
      const slot = this.take(puddle.cls);
      held = { puddle, slot, step: -1, threshold: 1, rippleCount: 0, area: 0, alpha: 1 };
      this.held.set(puddle.id, held);
      fieldInto(puddle, slot.cells);
      slot.image.setPosition(puddle.x - puddle.radius, puddle.y - puddle.radius).setCrop(0, 0, puddle.radius * PUDDLE_CELL_PX, puddle.radius * PUDDLE_CELL_PX).setVisible(true);
      for (const ripple of slot.ripples) ripple.placed = false;
    }
    if (held.alpha !== alpha) {
      held.alpha = alpha;
      held.slot.image.setAlpha(alpha);
      for (const ripple of held.slot.ripples) ripple.image.setAlpha(alpha);
    }
    const step = Math.round(fill * FILL_STEPS);
    if (step !== held.step) {
      held.step = step;
      held.threshold = cutOf(step / FILL_STEPS, peakOf(puddle));
      this.paint(held);
    }
    this.ripples(held, frame);
  }

  /**
   * Takes a slot of a class from the pool, or makes one at the class's largest size.
   *
   * @param cls The class.
   * @returns The slot, its image hidden.
   */
  private take(cls: PuddleClass): Slot {
    const spare = this.pool[cls].pop();
    if (spare !== undefined) return spare;
    const radius = this.side[cls];
    const pixelSide = radius * PUDDLE_CELL_PX;
    const key = `weather:puddle:${cls}:${this.count}`;
    this.count += 1;
    const texture = this.scene.textures.createCanvas(key, pixelSide, pixelSide) as Phaser.Textures.CanvasTexture;
    const image = this.scene.add.image(0, 0, key).setOrigin(0, 0).setDepth(this.depth).setVisible(false);
    const ripples: Ripple[] = [];
    for (let i = 0; i < SLOT_RIPPLES; i += 1) {
      ripples.push({ image: this.scene.add.image(0, 0, RIPPLE_KEY, 0).setOrigin(0, 0).setDepth(this.depth + 0.00001).setVisible(false), cycle: -1, x: 0, y: 0, placed: false });
    }
    return { cls, key, texture, image, pixels: new ImageData(pixelSide, pixelSide), cells: new Float32Array(radius * radius), ripples };
  }

  /**
   * Hides a held puddle and gives its slot back to the pool.
   *
   * @param held The puddle held.
   */
  private release(held: Held): void {
    held.slot.image.setVisible(false).setAlpha(1);
    for (const ripple of held.slot.ripples) ripple.image.setAlpha(1);
    for (const ripple of held.slot.ripples) ripple.image.setVisible(false);
    this.pool[held.puddle.cls].push(held.slot);
  }

  /**
   * Paints a puddle at its threshold and counts the ripples its area holds.
   *
   * @param held The puddle held.
   */
  private paint(held: Held): void {
    paintPuddle(held.puddle, held.slot, held.threshold);
    held.area = this.areaOf(held);
    held.rippleCount = Math.max(1, Math.min(SLOT_RIPPLES, Math.round(held.area / 900)));
  }

  /**
   * How many pixels of ground the painted puddle covers.
   *
   * @param held The puddle held.
   * @returns The area in pixels.
   */
  private areaOf(held: Held): number {
    let inside = 0;
    for (let i = 0; i < held.slot.cells.length; i += 1) {
      if ((held.slot.cells[i] as number) > held.threshold) inside += 1;
    }
    return inside * PUDDLE_CELL_PX * PUDDLE_CELL_PX;
  }

  /**
   * Plays the ripples of a puddle: each of its rings runs a cycle of its own length, is placed where the whole ring lies in
   * the water when the cycle begins, and shows the six frames across the first part of the cycle.
   *
   * @param held The puddle held.
   * @param frame The frame.
   */
  private ripples(held: Held, frame: PuddleFrame): void {
    const list = held.slot.ripples;
    const playing = frame.rain > RAIN_ON_ABOVE && frame.detail !== "off";
    const rate = frame.detail === "reduced" ? 0.5 : 1;
    for (let i = 0; i < list.length; i += 1) {
      const ripple = list[i] as Ripple;
      if (!playing || i >= held.rippleCount) {
        if (ripple.image.visible) ripple.image.setVisible(false);
        continue;
      }
      const period = (RIPPLE_PERIOD_MS * (0.8 + 0.6 * (((held.puddle.id * 7 + i * 13) % 10) / 10))) / rate;
      const clock = frame.now / period + ((held.puddle.id * 5 + i * 3) % 17) / 17;
      const cycle = Math.floor(clock);
      const phase = clock - cycle;
      if (ripple.cycle !== cycle || !ripple.placed) {
        ripple.cycle = cycle;
        ripple.placed = this.place(held, ripple, i, cycle);
      }
      if (!ripple.placed || phase > RIPPLE_ACTIVE) {
        if (ripple.image.visible) ripple.image.setVisible(false);
        continue;
      }
      const index = Math.min(RIPPLE_FRAMES - 1, Math.floor((phase / RIPPLE_ACTIVE) * RIPPLE_FRAMES));
      ripple.image.setFrame(index).setPosition(ripple.x, ripple.y).setVisible(true);
    }
  }

  /**
   * Finds a place for a ring to begin: the puddle's cells hashed, the first where the ring's four extremes and its centre lie
   * in the water.
   *
   * @param held The puddle held.
   * @param ripple The ripple.
   * @param slot The ripple's number in its puddle.
   * @param cycle The cycle it is placed for.
   * @returns True when a place was found.
   */
  private place(held: Held, ripple: Ripple, slot: number, cycle: number): boolean {
    const puddle = held.puddle;
    const side = puddle.radius;
    const cells = held.slot.cells;
    const inside = (px: number, py: number): boolean => {
      const cx = Math.floor(px / PUDDLE_CELL_PX);
      const cy = Math.floor(py / PUDDLE_CELL_PX);
      return cx >= 0 && cy >= 0 && cx < side && cy < side && (cells[cy * side + cx] as number) > held.threshold + 0.04;
    };
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const roll = Math.imul(puddle.id * 977 + slot * 131 + cycle * 7919 + attempt * 104729, HASH_A) >>> 0;
      const px = (roll % (side * PUDDLE_CELL_PX)) | 0;
      const py = ((roll >>> 12) % (side * PUDDLE_CELL_PX)) | 0;
      if (inside(px, py) && inside(px - RIPPLE_RX, py) && inside(px + RIPPLE_RX, py) && inside(px, py - RIPPLE_RY) && inside(px, py + RIPPLE_RY) && inside(px - RIPPLE_RX * 0.7, py - RIPPLE_RY * 0.7) && inside(px + RIPPLE_RX * 0.7, py + RIPPLE_RY * 0.7)) {
        ripple.x = puddle.x - puddle.radius + px - RIPPLE_W / 2;
        ripple.y = puddle.y - puddle.radius + py - RIPPLE_H / 2;
        return true;
      }
    }
    return false;
  }
}
